import React from "react";
import { Control, Controller } from "react-hook-form";

import { capitalize } from "lodash";

import { SliderProps, css, styled } from "@mui/material";

import { RangeSlider } from "@/components/form/range-slider/RangeSlider";
import { ConditionalWrapper } from "@/shared/ConditionalWrapper";
import { ThemeProviderWithPrimaryPink } from "@/shared/ThemeProviderWithPrimaryPink";

import { SearchInputLabel } from "./SearchInputLabel";

type PropTypes = {
  labelPrefix: string;
  unitLabel: string;
  control: Control;
  controllerName: string;
  minMaxValues: [number, number];
  minMaxLabels?: boolean;
  pinkVersion?: boolean;
};

const StyledRangeSlider = styled(RangeSlider)<SliderProps>(({ theme }) => {
  return css`
    .MuiSlider-rail {
      background-color: ${theme.palette.grey1[200]};
      filter: brightness(120%);
    }
  `;
});

export const SearchRangeSlider: React.FC<PropTypes> = ({
  unitLabel,
  labelPrefix,
  control,
  controllerName,
  minMaxValues,
  minMaxLabels,
  pinkVersion = false,
}) => {
  const [min, max] = minMaxValues;

  return (
    <ConditionalWrapper
      on={pinkVersion}
      Wrapper={({ children }) => {
        return (
          <ThemeProviderWithPrimaryPink>
            {children}
          </ThemeProviderWithPrimaryPink>
        );
      }}
    >
      <Controller
        control={control}
        name={controllerName}
        defaultValue={[min, max]}
        render={({ field: { value, onChange } }) => {
          const sliderUnactive = value[0] === min && value[1] === max;

          return (
            <>
              <SearchInputLabel
                minMaxValues={minMaxValues}
                minMaxLabels={minMaxLabels}
                value={value}
                units={unitLabel}
                dataTestId={`${controllerName}RangeLabel`}
              >
                {labelPrefix}
              </SearchInputLabel>
              <StyledRangeSlider
                sliderUnactive={sliderUnactive}
                min={min}
                max={max}
                value={value}
                onChange={onChange}
                getAriaLabel={(index) =>
                  `${capitalize(controllerName)} ${
                    index === 0 ? "minimum" : "maximum"
                  }`
                }
                getAriaValueText={(value: number) => {
                  if (value === min) return "min";
                  return String(value);
                }}
                data-testid={`${controllerName}Range`}
                {...(minMaxLabels
                  ? {
                      valueLabelFormat: (value) => {
                        if (value === min) return "min";
                        if (value === max) return "max";
                        return value;
                      },
                    }
                  : {})}
              />
            </>
          );
        }}
      />
    </ConditionalWrapper>
  );
};
